import React, { useEffect } from 'react';
import { Router, Route } from 'react-router-dom';
import usePortalAuth from './auth/usePortalAuth';
import LoginDesktop from './components/Login/LoginDesktop';
import LoginPortal from './components/Login/LoginPortal';
import Success from './components/Login/Success';
import DetectBrowser from './components/Signup/DetectBrowser';
import Dashboard from './components/Dashboard/Dashboard';
import LandingPage from './components/LandingPages/LandingPage';
import CheckingOutsideLinkNotice from './components/LandingPages/CheckingOutsideLinkNotice';
import TrainingItemView from './components/Dashboard/Training/TrainingItemView';
import { QueryClient, useQuery } from 'react-query';
import Signup from './components/Signup/Signup';
import Support from './components/Support/Support';
import BrowserConfig from './components/BrowserConfig/BrowserConfig';
import Register from './components/Signup/Register';
import NewSignUp from './components/Signup/NewSignUp/NewSignUp';
import Error from './components/Dashboard/Error';
import MoreInfo from './components/MoreInfo/MoreInfo';
import NewUserLandingPage from './components/Signup/NewUserLandingPage/NewUserLandingPage';
import DemoPage from './components/Signup/NewUser/DemoPage/DemoPage';
import InitialDownloads from './components/InitialDownloads/InitialDownloads';
import history from './utils/history';
import Activation from './components/Activation/Activation';
import ErrorUserNotFound from './components/Dashboard/Error/ErrorUserNotFound';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CssBaseline from '@material-ui/core/CssBaseline';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Switch from '@material-ui/core/Switch';
import Brightness4Icon from '@material-ui/icons/Brightness4';
import useStickyState from './components/Dashboard/CustomHooks/useStickyState';
import NotFound from './components/ErrorPage/NotFound';
import HelpHome from './components/HelpPages/HelpHome';
import CampaignSignUpForm from './components/PhishingCampaign/CampaignSignUpForm';
import ThankYou from './components/PhishingCampaign/ThankYou';
import ExtensionWelcomePage from './components/ExtensionWelcomePage';
import PrivateRoute from './components/Dashboard/Routes/PrivateRoute';
import { fetchApiData } from './utils/service';
import RedirectOnNotFoundTopLevel from './components/RedirectOnNotFoundTopLevel';
import EmailLogin from './components/EmailLogin/EmailLogin';
import DuracellLogin from './components/Login/BrandedLogins/DuracellLogin';
import PhishCloudLogin from './components/Login/BrandedLogins/PhishCloudLogin';
import PcAuthStart from './components/Login/PcAuthStart';
import PcAuthCallback from './components/Login/PcAuthCallback';
import LoggedOut from './components/Login/LoggedOut';
import {
  DARK_MODE_PREFERENCE_MAP_KEY,
  LANGUAGE_PREFERENCE_MAP_KEY,
  getUserPreferenceScope,
  normalizePreferenceMap,
} from './utils/userPreferences';

const App = (props) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const { user } = usePortalAuth();
  const preferenceScope = getUserPreferenceScope(user);
  const initialLanguage = 'EN';
  const [darkModeByUser, setDarkModeByUser] = useStickyState({}, DARK_MODE_PREFERENCE_MAP_KEY);
  const [preferredLanguageByUser, setPreferredLanguageByUser] = useStickyState(
    {},
    LANGUAGE_PREFERENCE_MAP_KEY
  );

  const normalizedDarkModeByUser = normalizePreferenceMap(darkModeByUser);
  const normalizedPreferredLanguageByUser = normalizePreferenceMap(preferredLanguageByUser);

  const hasStoredPreferredLanguage = Object.prototype.hasOwnProperty.call(
    normalizedPreferredLanguageByUser,
    preferenceScope
  );

  const darkMode = Object.prototype.hasOwnProperty.call(normalizedDarkModeByUser, preferenceScope)
    ? !!normalizedDarkModeByUser[preferenceScope]
    : false;

  const preferredLanguage = hasStoredPreferredLanguage
    ? normalizedPreferredLanguageByUser[preferenceScope]
    : initialLanguage;

  const setDarkMode = (nextValue) => {
    setDarkModeByUser((previousMapState) => {
      const previousMap = normalizePreferenceMap(previousMapState);
      const currentValue = Object.prototype.hasOwnProperty.call(previousMap, preferenceScope)
        ? !!previousMap[preferenceScope]
        : false;
      const resolvedValue = typeof nextValue === 'function' ? nextValue(currentValue) : nextValue;

      return {
        ...previousMap,
        [preferenceScope]: !!resolvedValue,
      };
    });
  };

  const setPreferredLanguage = (nextValue) => {
    setPreferredLanguageByUser((previousMapState) => {
      const previousMap = normalizePreferenceMap(previousMapState);
      const currentValue = Object.prototype.hasOwnProperty.call(previousMap, preferenceScope)
        ? previousMap[preferenceScope]
        : initialLanguage;
      const resolvedValue = typeof nextValue === 'function' ? nextValue(currentValue) : nextValue;
      const normalizedValue = String(resolvedValue || '')
        .trim()
        .toUpperCase();

      return {
        ...previousMap,
        [preferenceScope]: normalizedValue || initialLanguage,
      };
    });
  };

  const paletteType = darkMode ? 'dark' : 'light';
  //const mainPrimaryColor = darkState ? '#303030' : '#061739';
  //const mainSecondaryColor = darkState ? '#303030' : '#f50057';
  const sideNavColor = darkMode ? '#303030' : '#061739';
  //console.log(user)
  
  // Update body class for dark mode
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  //const userSettingsData = useQuery('user-settings', async () => {

  //   const token = await getAccessTokenSilently();
  //   console.log(token)
  //   return fetchApiData('https://portalapi-v2.phishcloud.io/api/portalapi-v2', token, 'getUserSettings');
  // });

  // if (!userSettingsData.isLoading) {
  // console.log(userSettingsData.data)
  // console.log("Testing")
  // }

  const theme = createMuiTheme({
    palette: {
      type: paletteType,
      primary: {
        main: darkMode ? '#90caf9' : '#3f51b5',
      },
      secondary: {
        main: darkMode ? '#f48fb1' : '#f50057',
      },
      background: {
        default: darkMode ? '#121212' : '#fafafa',
        paper: darkMode ? '#1e1e1e' : '#fff',
      },
      text: {
        primary: darkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
        secondary: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
      },
      divider: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      sideNav: {
        main: sideNavColor,
      },
    },
  });

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router history={history}>
          <RedirectOnNotFoundTopLevel history={props.history} location={props.location} redirectTo="/Dashboard/">
            <Route
              exact
              path="/"
              component={(props) => (
                <LoginPortal
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              exact
              path="/Login"
              component={(props) => (
                <EmailLogin
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />
            <Route
              exact
              path="/auth/start"
              component={(props) => (
                <PcAuthStart
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />
            <Route
              exact
              path="/auth/callback"
              render={(props) => (
                <PcAuthCallback
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              exact
              path="/Duracell"
              component={(props) => (
                <DuracellLogin
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              exact
              path="/PhishCloudLogin"
              component={(props) => (
                <PhishCloudLogin
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              exact
              path="/Error"
              component={(props) => (
                <Error history={props.history} location={props.location} preferredLanguage={props.preferredLanguage} />
              )}
            />
            <Route
              exact
              path="/ErrorUserNotFound"
              component={(props) => (
                <ErrorUserNotFound
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />
            <Route
              exact
              path="/MoreInfo"
              component={(props) => (
                <MoreInfo
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              path="/PortalSignIn"
              component={(props) => (
                <LoginPortal
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />
            <Route
              exact
              path="/LoggedOut"
              component={(props) => (
                <LoggedOut
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <PrivateRoute
              path="/Dashboard"
              component={(props) => (
                <Dashboard
                  history={props.history}
                  location={props.location}
                  toggleDarkMode={setDarkMode}
                  darkMode={darkMode}
                  preferredLanguage={preferredLanguage}
                  setPreferredLanguage={setPreferredLanguage}
                  hasStoredPreferredLanguage={hasStoredPreferredLanguage}
                  user={user}
                />
              )}
            />

            <Route
              path="/LandingPage"
              component={(props) => (
                <LandingPage
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              path="/PhishingSucks"
              component={(props) => (
                <CampaignSignUpForm
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              path="/ThankYou"
              component={(props) => (
                <ThankYou
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              path="/Welcome"
              component={(props) => (
                <ExtensionWelcomePage
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              path="/CheckingOutsideLinkNotice"
              component={(props) => (
                <CheckingOutsideLinkNotice
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              path="/Signup"
              component={(props) => (
                <Signup history={props.history} location={props.location} preferredLanguage={props.preferredLanguage} />
              )}
            />
            <Route
              path="/Register"
              component={(props) => (
                <Register
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />
            <Route
              path="/SuccessEmailVerification"
              component={(props) => <NewUserLandingPage history={props.history} location={props.location} />}
            />

            {/* <Route
              path="/DemoPage"
              component={(props) => <DemoPage history={props.history} location={props.location} />}
            /> */}

            <Route
              path="/InitialDownloads"
              component={(props) => (
                <InitialDownloads
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />
            <Route
              path="/Help"
              component={(props) => (
                <HelpHome
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />
            <Route
              path="/BrowserConfig"
              component={(props) => (
                <BrowserConfig
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              path="/Support"
              component={(props) => (
                <Support
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />
            <Route
              path="/BrowserCheck"
              component={(props) => (
                <DetectBrowser
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

            <Route
              path="/DesktopSignIn"
              component={(props) => (
                <LoginDesktop
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />
            <Route
              path="/DesktopSignInResult"
              component={(props) => (
                <Success
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />
            <Route
              path="/Activation"
              component={(props) => (
                <Activation
                  history={props.history}
                  location={props.location}
                  preferredLanguage={props.preferredLanguage}
                />
              )}
            />

          </RedirectOnNotFoundTopLevel>
        </Router>
      </ThemeProvider>
    </div>
  );
};
export default App;

//------------------For Future Release----------------//

//   < Route path = "/Training" component = { props => < TrainingItemView history = { props.history } location = { props.location } />} />
/*


useEffect(() => {
    getToken();
}, [loading]);
*/

//<ListItem button
//     className={isActive('/Alerts')}
//     key="Alerts"
//     component={Link}
//     to={'/Alerts'}>
//     <ListItemIcon className={classes.list}>
//             <FontAwesomeIcon  icon={['fas', 'bell']} size='lg' />
//     </ListItemIcon>
//     <ListItemText> Alerts</ListItemText>
// </ListItem>

//< AdminRoute path = '/Alerts' location = { location } component = { props => < Alerts history = { props.history } location = { props.location } />} />
//    < AdminRoute path = '/AlertsDetail' location = { location } component = { props => < AlertsDetail history = { props.history } location = { props.location } />} />

//--------------------------------------------------------------------------------------//

//if (loading) {
//    <div >
//        Loading...
//        </div>
//    console.log('loading')
//}

//const toggleNavbar = () => {
//    setCollapsed(!collapsed);
//        const elemBody = document.getElementById("mainBody");
//        if (collapsed === true) {
//            elemBody.className = "mainBodyOpen"
//        } else {
//            elemBody.className = "mainBodyClosed"
//        }
//}

//{ renderSideNav(location, history) }
//{ renderMainBody(location, history) }
